import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import "./style.css";

// Generic searchable code picker for company-defined catalog lists (worker
// classes, absence codes, field log types, ...) — mirrors
// SearchablePayrollDepartmentSelect's control/dropdown UX but takes its data
// source and rendering via props instead of being hardcoded to one resource.
export default function SearchableCodeSelect({
  label,
  required,
  value,
  companyUuid,
  onChange,
  fetchOptions,
  getOptionValue = (option) => option.code,
  getOptionKey = (option) => option.uuid || option.code,
  formatOptionLabel = (option) =>
    option.description ? `${option.code} - ${option.description}` : option.code,
  renderOption,
  className = "",
  placeholder = "Search...",
  searchPlaceholder = "Type to search...",
  emptyMessage = "No results found",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  // null while the initial "does this company have any catalog data at all"
  // check is in flight — defaults to the picker until proven otherwise, so a
  // slow/failed check doesn't strand the field with nothing rendered.
  const [catalogHasData, setCatalogHasData] = useState(true);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  // A company that hasn't set up this catalog resource yet (e.g. no Worker
  // Classes configured) would otherwise show a picker with zero results and
  // no way to enter a code — fall back to a plain text input, matching the
  // pre-picker behavior, when the catalog is confirmed empty.
  useEffect(() => {
    let cancelled = false;
    const checkCatalog = async () => {
      try {
        const result = await fetchOptions("", companyUuid);
        if (!cancelled && result.status === 200 && Array.isArray(result.data)) {
          setCatalogHasData(result.data.length > 0);
        }
      } catch (error) {
        console.error("Error checking catalog data:", error);
      }
    };
    checkCatalog();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUuid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // The control can sit inside a scrollable modal body, so the dropdown is
  // portaled to <body> and positioned via the control's rect rather than
  // relying on CSS absolute positioning, which a scrolling ancestor clips.
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      setLoading(true);
      try {
        const result = await fetchOptions(searchTerm, companyUuid);
        if (result.status === 200 && Array.isArray(result.data)) {
          setOptions(result.data);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 300);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isOpen, companyUuid]);

  useEffect(() => {
    if (
      value &&
      (!selectedOption || getOptionValue(selectedOption) !== value)
    ) {
      const loadSelectedOption = async () => {
        try {
          const result = await fetchOptions(value, companyUuid);
          if (result.status === 200 && Array.isArray(result.data)) {
            const match = result.data.find(
              (option) => getOptionValue(option) === value
            );
            if (match) setSelectedOption(match);
          }
        } catch (error) {
          console.error("Error loading selected option:", error);
        }
      };
      loadSelectedOption();
    } else if (!value && selectedOption) {
      setSelectedOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, companyUuid]);

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange("");
    setSearchTerm("");
  };

  if (!catalogHasData) {
    return (
      <div className={`searchable-code-select ${className}`}>
        {label && (
          <label className="searchable-code-select__label">
            {label}
            {required && <span className="danger">&nbsp;*</span>}
          </label>
        )}
        <input
          value={value || ""}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div className={`searchable-code-select ${className}`} ref={dropdownRef}>
      {label && (
        <label className="searchable-code-select__label">
          {label}
          {required && <span className="danger">&nbsp;*</span>}
        </label>
      )}

      <div
        className={`searchable-code-select__control ${
          isOpen ? "searchable-code-select__control--open" : ""
        } ${disabled ? "searchable-code-select__control--disabled" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="searchable-code-select__value">
          {selectedOption ? (
            <>
              <span>{formatOptionLabel(selectedOption)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="searchable-code-select__clear"
                  onClick={handleClear}
                  aria-label="Clear selection"
                >
                  <X size={16} />
                </button>
              )}
            </>
          ) : value ? (
            <span>{value}</span>
          ) : (
            <span className="searchable-code-select__placeholder">
              {placeholder}
            </span>
          )}
        </div>
        <Search size={16} className="searchable-code-select__icon" />
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="searchable-code-select__dropdown"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            <div className="searchable-code-select__search">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                className="searchable-code-select__search-input"
              />
            </div>

            <div className="searchable-code-select__options">
              {loading ? (
                <div className="searchable-code-select__loading">
                  Loading...
                </div>
              ) : options.length === 0 ? (
                <div className="searchable-code-select__empty">
                  {emptyMessage}
                </div>
              ) : (
                options.map((option) => (
                  <div
                    key={getOptionKey(option)}
                    className="searchable-code-select__option"
                    onClick={() => handleSelect(option)}
                  >
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        <div className="searchable-code-select__option-code">
                          {option.code}
                        </div>
                        <div className="searchable-code-select__option-name">
                          {option.description}
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
